import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext(null);

// Map between localized URL paths
const URL_MAP = {
  toEn: (path) => {
    if (path.startsWith("/en")) return path;
    if (path.startsWith("/proyek")) return path.replace(/^\/proyek/, "/en/projects");
    if (path === "/" || path === "") return "/en";
    return `/en${path}`;
  },
  toId: (path) => {
    if (path.startsWith("/en/projects")) return path.replace(/^\/en\/projects/, "/proyek");
    if (path === "/en" || path === "/en/") return "/";
    if (path.startsWith("/en/")) return path.replace(/^\/en/, "");
    if (path === "/en") return "/";
    return path;
  },
};

function detectLangFromPath(pathname) {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (pathname === "/" || pathname.startsWith("/proyek")) return "id";
  return null; // admin or anything else — fall back to stored preference
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "id";
    const fromUrl = detectLangFromPath(window.location.pathname);
    return fromUrl || localStorage.getItem("site_lang") || "id";
  });

  // Sync lang ← URL on every navigation (only for public pages)
  useEffect(() => {
    const fromUrl = detectLangFromPath(location.pathname);
    if (fromUrl && fromUrl !== lang) {
      setLangState(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("site_lang", lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = (bilingual) => {
    if (!bilingual) return "";
    if (typeof bilingual === "string") return bilingual;
    return bilingual[lang] || bilingual.id || bilingual.en || "";
  };

  // Setting language also rewrites the URL on public routes so it stays bilingual
  const setLang = (newLang) => {
    if (newLang === lang) return;
    setLangState(newLang);
    const path = location.pathname;
    const isPublic = path === "/" || path.startsWith("/proyek") || path === "/en" || path.startsWith("/en");
    if (isPublic) {
      const target = newLang === "en" ? URL_MAP.toEn(path) : URL_MAP.toId(path);
      if (target !== path) navigate(target, { replace: true });
    }
  };

  const toggle = () => setLang(lang === "id" ? "en" : "id");

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
